'use client';

import { Delete as DeleteIcon, Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridAlignment, getGridDateOperators } from '@mui/x-data-grid-premium';
import moment from 'moment-timezone';

import { ContractDocumentsColumns } from '../../../types';
import { formatEnumValueForDisplay } from '../../../utils';
import { ConfirmationModal } from '../../modals';
import { AbstractModal } from './abstractModal';
import { ViewDocumentButton } from './viewDocumentButton';

const dateOperators = getGridDateOperators();

export const getContractDocumentsColumns = ({
	showModal,
	rows,
	setRows,
	deleteDocument,
	setShowSnackbar,
	getSignedUrl,
	setShowDialog,
	theme,
	hasComponentAccess,
	handleAuditHistory,
}: ContractDocumentsColumns) => [
	{
		field: 'actions',
		type: 'actions',
		headerName: 'Actions',
		width: 120,
		cellClassName: 'actions',
		renderCell: (params) => {
			if (params.rowNode.groupingField === 'accountId') {
				return [
					<Button
						key='edit-abstract'
						onClick={() =>
							setShowDialog(
								<AbstractModal
									accountId={params.rowNode.groupingKey}
									handleClose={() => setShowDialog(null)}
									theme={theme}
								/>,
							)
						}
					>
						ABSTRACT
					</Button>,
				];
			}
			if (params.rowNode.type === 'group') return [];
			const actions = [
				<GridActionsCellItem
					key='edit'
					icon={<EditIcon />}
					label='Edit'
					className='textPrimary'
					onClick={() => showModal(params.row)}
					color='inherit'
				/>,
				<GridActionsCellItem
					key='delete'
					icon={<DeleteIcon />}
					label='Delete'
					onClick={async () => {
						const response = await deleteDocument({
							variables: {
								id: params.row.id,
							},
							context: {
								apiVersion: 'v2',
								appName: process.env.NEXT_PUBLIC_APP_NAME,
							},
						});
						if (response?.data?.deleteContractDocument) {
							setRows(rows.filter((row) => row.id !== params.row.id));
							setShowSnackbar({
								message: 'Document successfully deleted',
								severity: 'success',
								autoHideDuration: 3000,
							});
						} else {
							setShowSnackbar({
								message: 'Document failed to delete',
								severity: 'error',
								autoHideDuration: 3000,
							});
						}
					}}
					color='inherit'
				/>,
			];

			if (hasComponentAccess('AuditHistoryTable')) {
				actions.push(
					<IconButton onClick={() => handleAuditHistory(params.row.id, params.row.timezone)}>
						<Tooltip title='View Audit History'>
							<HistoryIcon />
						</Tooltip>
					</IconButton>,
				);
			}

			return actions;
		},
	},
	{
		field: 'accountId',
		headerName: 'Account ID',
		type: 'string',
		width: 130,
		renderCell: (params) => {
			if (params.rowNode.type === 'group') return params.value;
			return (
				<p>
					{params.value} -
					<GridActionsCellItem
						key='edit-abstract'
						icon={
							<Typography style={{ color: 'blue', fontSize: '12px' }} variant='body1'>
								ABSTRACT
							</Typography>
						}
						label='Edit'
						className='textPrimary'
						onClick={() =>
							setShowDialog(
								<AbstractModal
									theme={theme}
									accountId={params.value}
									handleClose={() => setShowDialog(null)}
								/>,
							)
						}
						color='inherit'
					/>
				</p>
			);
		},
	},
	{
		field: 'facilityName',
		headerName: 'Facility Name',
		type: 'string',
		width: 200,
	},
	{
		field: 'facilityNumber',
		headerName: 'Facility Number',
		type: 'string',
		width: 125,
	},
	{
		field: 'subscriptionName',
		headerName: 'Sub. Name',
		type: 'string',
	},
	{
		field: 'subscriptionStatus',
		headerName: 'Sub. Status',
		type: 'string',
	},
	{
		field: 'contractDate',
		headerName: 'Contract Date',
		editable: true,
		valueGetter: (params) => {
			if (!params.value) return null;
			const convertedTime = moment(params.value, 'YYYY-MM-DD');
			return convertedTime.toDate();
		},
		renderCell: (params) => {
			if (!params.value) return null;
			return moment(params.value, 'YYYY-MM-DD').format('MM/DD/YYYY');
		},
		filterOperators: dateOperators,
		type: 'date',
	},
	{
		field: 'subscriberType',
		headerName: 'Sub. Type',
		type: 'string',
		valueGetter: (params) => {
			if (params.value) {
				return formatEnumValueForDisplay(params.value);
			}
			return '';
		},
	},
	{
		field: 'contractType',
		headerName: 'Contract Type',
		type: 'string',
		valueGetter: (params) => {
			if (params.value) {
				return formatEnumValueForDisplay(params.value);
			}
			return '';
		},
		width: 125,
	},
	{
		field: 'landlordEntity',
		headerName: 'Landlord Entity',
		type: 'string',
		width: 125,
	},
	{
		field: 'subscriberEntity',
		headerName: 'Sub. Entity',
		type: 'string',
	},
	{
		field: 'commencementDate',
		headerName: 'Commencement Date',
		valueGetter: (params) => {
			if (!params.value) return null;
			const convertedTime = moment(params.value, 'YYYY-MM-DD');
			return convertedTime.toDate();
		},
		renderCell: (params) => {
			if (!params.value) return null;
			return moment(params.value, 'YYYY-MM-DD').format('MM/DD/YYYY');
		},
		width: 150,
		type: 'date',
	},
	{
		field: 'expirationDate',
		headerName: 'Expiration Date',
		valueGetter: (params) => {
			if (!params.value) return null;
			const convertedTime = moment(params.value, 'YYYY-MM-DD');
			return convertedTime.toDate();
		},
		renderCell: (params) => {
			if (!params.value) return null;
			return moment(params.value, 'YYYY-MM-DD').format('MM/DD/YYYY');
		},
		width: 150,
		type: 'date',
	},
	{
		field: 'parkingContractSection',
		headerName: 'Parking Contract Section',
		width: 200,
		type: 'string',
	},
	{
		field: 'documentPath',
		headerName: 'Document',
		resizable: false,
		sortable: false,
		filterable: false,
		disableColumnMenu: true,
		headerAlign: 'center' as GridAlignment,
		align: 'center' as GridAlignment,
		type: 'string',
		width: 100,
		renderCell: (params) => {
			if (!params.row.documentPath || params.rowNode.type === 'group') {
				return null;
			}
			return (
				<Box
					onClick={async () => {
						const response = await getSignedUrl({
							variables: {
								type: 'get',
								key: params.row.documentPath,
							},
							context: {
								apiVersion: 'v2',
							},
						});

						if (!response?.data?.contractDocumentSignedUrl?.url) {
							throw new Error('Failed to get signed url');
						}

						setShowDialog(
							<ConfirmationModal
								content={response.data.contractDocumentSignedUrl.url}
								title='Contract Document'
								type={params.row.documentContentType}
								dismissText='Close'
								confirmText=''
								handleDismiss={() => setShowDialog(null)}
								handleConfirm={null}
							/>,
						);
					}}
				>
					<ViewDocumentButton />
				</Box>
			);
		},
	},
];
